import { getTryFirstFlow } from "@helpers/localStorage"
import { trackSelfDescribingEvent } from "@snowplow/browser-tracker"
import { redirect } from "react-router-dom"

const HAIRSTYLE_RELATED_FLOWS = ["hairstyle", "looksmaxxing"]

/**
 * Creates the onboarding "navigate to next page" loader action for a given
 * ordered route list. Used by the baseline action and every A/B variant —
 * variants only differ in which route array they pass in.
 *
 * Why a factory rather than three near-identical files:
 *   The skip rules, snowplow event shape, and step-counting math must stay
 *   in sync across variants for our analytics to be comparable. Cloning the
 *   whole file per variant means every fix risks landing on one copy and
 *   silently skewing the others. Centralizing here keeps the variants down
 *   to "what's the route order" — the only thing an A/B test should
 *   actually be changing.
 */
export function createNavigateToNextOnboardingPageAction(
  orderedRoutes: readonly string[],
) {
  function shouldSkipPage(page: string): boolean {
    if (page === "hairstyleFrequency") {
      return !HAIRSTYLE_RELATED_FLOWS.includes(getTryFirstFlow())
    }
    return false
  }

  function getEffectiveStepsTotal(): number {
    return orderedRoutes.filter((page) => !shouldSkipPage(page)).length
  }

  function getCurrentIndex(url: string): number | undefined {
    const pathname = new URL(url).pathname
    const current = pathname.split("/").pop()
    if (!current) return
    return orderedRoutes.indexOf(current)
  }

  function getNext(url: string): string | undefined {
    const currentIndex = getCurrentIndex(url)
    if (currentIndex === undefined) return
    return orderedRoutes[currentIndex + 1]
  }

  function getNextPathName({
    next,
    url,
  }: {
    next: string
    url: string
  }): string {
    const currentPathname = new URL(url).pathname
    const paths = currentPathname.split("/")
    const basePathname = paths.slice(0, -1).join("/")
    return basePathname + "/" + next
  }

  function getEffectiveStepNumber(
    currentIndex: number | undefined,
  ): number | undefined {
    if (currentIndex === undefined) return undefined
    let effectiveStep = 0
    for (let i = 0; i <= currentIndex; i++) {
      if (!shouldSkipPage(orderedRoutes[i])) {
        effectiveStep++
      }
    }
    return effectiveStep
  }

  function trackFormDataWithSnowplow({
    formData,
    url,
  }: {
    formData: FormData
    url: string
  }) {
    const entries = Array.from(formData.entries())
    const keys = [...new Set(entries.map(([key]) => key))]

    const steps_total = getEffectiveStepsTotal()
    const currentIndex = getCurrentIndex(url)
    const step = getEffectiveStepNumber(currentIndex)

    for (const key of keys) {
      const data = {
        answers: formData.getAll(key),
        question: key,
        step,
        steps_total,
        url,
      }

      trackSelfDescribingEvent({
        event: {
          data,
          schema: "iglu:com.asanarebel/onboarding_event/jsonschema/1-0-0",
        },
      })
    }
  }

  return async function navigateToNextOnboardingPageAction({
    request,
  }: {
    request: Request
  }) {
    const formData = await request.formData()

    trackFormDataWithSnowplow({ formData, url: request.url })

    let next = getNext(request.url)

    while (next && shouldSkipPage(next)) {
      const currentIndex = orderedRoutes.indexOf(next)
      next = orderedRoutes[currentIndex + 1]
    }

    if (!next) {
      return redirect("/sign-up/password")
    }

    const nextPathname = getNextPathName({ next, url: request.url })

    return redirect(nextPathname)
  }
}
